import type { Chat } from "@/reducers/chatsSlice"
import type { Profile } from "@/reducers/profilesSlice"
import { resolveDriverDisplayName } from "./resolveDriverDisplayName"
import i18n from "@/lib/i18n/i18n"

type AccountChats = Record<string, { driverA?: string } | undefined>

/**
 * Take a JID like "taras_palamarchuk@drivers.chomar.pl" and return
 * "Taras Palamarchuk" — best-effort title-casing of the local part with
 * underscores and dots split into words. Returns undefined if the input
 * has no usable local part.
 */
export function formatJidLocalpartAsName(jid?: string): string | undefined {
  if (!jid) return undefined
  const local = jid.split("@")[0]
  if (!local) return undefined
  return local
    .split(/[_.]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(" ")
}

/**
 * Resolve a display name for the driver who sent a search-result document.
 *
 * Fallback chain:
 *  1. resolveDriverDisplayName via the user's local chats (handles driverA
 *     and room participants when the source chat is one the user is in)
 *  2. global profile lookup by the wizard's `author` field
 *  3. global profile lookup by `driverJid`
 *  4. title-cased local part of `author`
 *  5. title-cased local part of `chatJid` (driver MUC localpart)
 *  6. "Unknown"
 */
export function resolveSearchDriverName(args: {
  chatJid?: string
  driverJid?: string
  authorJid?: string
  chats: Chat[]
  profiles: Profile[]
  accountChats: AccountChats
}): string {
  const { chatJid, driverJid, authorJid, chats, profiles, accountChats } = args

  if (chatJid && chats.some((c) => c.jid === chatJid)) {
    return resolveDriverDisplayName({
      chatJid,
      driverJid,
      chats,
      profiles,
      accountChats,
    })
  }

  for (const candidate of [authorJid, driverJid]) {
    if (!candidate) continue
    const profile = profiles.find((p) => p.jid === candidate)
    if (!profile) continue
    if (profile.firstName && profile.lastName) {
      return `${profile.firstName} ${profile.lastName}`
    }
    if (profile.name) return profile.name
  }

  return (
    formatJidLocalpartAsName(authorJid) ||
    formatJidLocalpartAsName(chatJid) ||
    formatJidLocalpartAsName(driverJid) ||
    i18n.t("reports.shared.unknown")
  )
}
